import React, { useMemo, useRef, useEffect } from "react";
import classNames from "classnames";
import { IMaskInput } from "react-imask";

import { ComposantAllProps, getColorsClasses, getSizeClasses } from "../common";
import { uuidv4 } from "utils/uuid.utils";
import { usePreventWheelOnFocus } from "hooks/usePreventWheelOnFocus";

/** Masque email (équivalent text-mask-addons emailMask) : caractères courants email, un seul @ */
const EMAIL_MASK = /^[a-zA-Z0-9.+\-_]*@?[a-zA-Z0-9.\-]*$/;

/**
 * Convertit un masque au format text-mask (array) en pattern string imask.
 * Chaque élément : RegExp → 0 (digit), a (letter), * (any) ; string → caractère littéral.
 */
function textMaskArrayToImaskPattern(mask: string | (string | RegExp)[]): string | RegExp {
  if (typeof mask === "string") return mask;
  if (!Array.isArray(mask) || mask.length === 0) return /.*/;
  let pattern = "";
  for (const item of mask) {
    if (typeof item === "string") {
      pattern += item === "\\" ? "\\\\" : item;
    } else {
      const s = item.toString();
      if (s === "/\\d/" || s === "/[0-9]/") pattern += "0";
      else if (s === "/[a-zA-Z]/" || s === "/[a-z]/i") pattern += "a";
      else pattern += "*";
    }
  }
  return pattern || /.*/;
}

export interface InputAllProps extends ComposantAllProps {
  tabIndex?: number;
  autoComplete?: string;
  htmlType?: string;
  placeholder?: string;
  "data-custom-mask-decimal-limit"?: number;
  "data-is-number"?: boolean;
  maxLength?: number;
  step?: "1"; // Propriété pour gérer les secondes dans le calendarHoursSecondes
}

export const Input = React.forwardRef<HTMLInputElement, InputAllProps>(
  (
    { id, type = "text", value, onContextMenu, wviState, size, className, tooltip, ...restProps },
    ref
  ) => {
    // Les navigateurs propose tout seul de l'autocomplete sur les fields
    // Pour ignorer ça on devrait mettre [autocomplete = "off"]
    // Mais les navigateurs ignore cette dernière depuis peu
    // Alors on truande le système de la manière suivante
    const autocompleteKiller = useMemo(() => {
      return uuidv4();
    }, []);
    // La classe définissant la couleur dans un input est définie par le retour du wvi
    const colorClasses = getColorsClasses(wviState ? wviState : "");
    const sizeClasses = getSizeClasses(size ? size : "");
    const classes = classNames("input", className, colorClasses, sizeClasses);

    const notNullValue = value !== null && value !== undefined ? value : "";

    return (
      <input
        {...restProps}
        ref={ref}
        id={id}
        className={classes}
        type={type}
        value={notNullValue}
        data-value={notNullValue}
        onContextMenu={onContextMenu}
        title={tooltip}
        autoComplete={autocompleteKiller}
      />
    );
  }
);

export const InputNumber = React.forwardRef<HTMLInputElement, InputAllProps>((props, ref) => {
  const innerRef = useRef<HTMLInputElement | null>(null);
  usePreventWheelOnFocus(innerRef);

  // on synchronise les ref entre interne & externe.
  useEffect(() => {
    if (ref == null) return;

    if (typeof ref === "function") {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  });
  return <Input ref={innerRef} {...props} type="number" data-right-align="true" />;
});

export type InputMaskAllProps = InputAllProps & { mask?: string | string[] | (string | RegExp)[] };

export const InputMask = React.forwardRef<
  HTMLInputElement,
  InputAllProps & { mask?: string | string[] | (string | RegExp)[] }
>(
  (
    { id, type, mask, value, onContextMenu, wviState, size, className, tooltip, onChange, ...restProps },
    ref
  ) => {
    const isEmail = type === "email" && (mask === undefined || mask === null);
    const imaskOptions = useMemo(() => {
      if (isEmail) return { mask: EMAIL_MASK };
      if (mask === undefined || mask === null) return { mask: /.*/ };
      const resolved = textMaskArrayToImaskPattern(mask as string | (string | RegExp)[]);
      return typeof resolved === "string" ? { mask: resolved } : { mask: resolved };
    }, [isEmail, mask]);

    // La classe définissant la couleur dans un input est définie par le retour du wvi
    const colorClasses = getColorsClasses(wviState ? wviState : "");
    const sizeClasses = getSizeClasses(size ? size : "");
    const classes = classNames("input", className, colorClasses, sizeClasses);

    const notNullValue = value !== null && value !== undefined ? value : "";

    return (
      <>
        <IMaskInput
          {...restProps}
          {...imaskOptions}
          inputRef={ref}
          type="text"
          id={id}
          aria-label={id}
          className={classes}
          value={notNullValue}
          data-value={notNullValue}
          onContextMenu={onContextMenu}
          title={tooltip}
          unmask={false}
          onAccept={(maskedValue: string) => {
            onChange?.({ target: { value: maskedValue } } as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      </>
    );
  }
);

export default Input;
